

import { useEffect, useRef, useState } from 'react';
import type { ClipboardEvent, FormEvent, KeyboardEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { SplitAuthLayout } from '@/components/auth/SplitAuthLayout';
import { authApi } from '@/lib/api/auth.api';
import { useAuth } from '@/components/auth/AuthProvider';
import { useLoginSession } from '@/store/login-session.store';
import { useAuth as useAuthStore } from '@/store/auth.store';
import { organizationInvitesApi } from '@/lib/api/organization-invites.api';
import { organizationsApi } from '@/lib/api/organizations.api';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/hooks/query-keys';
import { isMobileAuthFlow, redirectToMobileApp } from '@/utils/mobile-auth';
import { isTauri } from '@/lib/utils/tauri';
import { getAdminReturnTarget, redirectToAdminReturnTarget } from '@/utils/auth/admin-handoff';
import { extractOrgSlug } from '@/utils/org-paths';
import type { User } from '@scrambl/shared';
import { createLogger } from '@/lib/logger';
import { AppLoader } from '@/lib/router/components';

const logger = createLogger('pages/login/confirmation/index');

// Import shadcn components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
// Add a function to check if the data is a User object
function isUserData(data: unknown): data is User {
  return data && typeof data === 'object' && 'id' in data && 'email' in data;
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export default function LoginConfirmationPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState<string>('');
  const [session, setSession] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isResending, setIsResending] = useState<boolean>(false);
  const [timeLeft, setTimeLeft] = useState<number>(30);
  const [canResend, setCanResend] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const { setUser } = useAuth();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const codeExpiryTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Get redirect URL from URL params or session store
  const redirectFromUrl = searchParams.get('redirect');
  const adminReturnTarget = getAdminReturnTarget(searchParams.toString());
  const isAddAccountFlow = searchParams.has('addAccount');

  // Get session data from Zustand store using individual selectors to avoid infinite loops
  const storeEmail = useLoginSession(state => state.email);
  const storeSession = useLoginSession(state => state.session);
  const storeRedirectUrl = useLoginSession(state => state.redirectUrl);
  const clearSession = useLoginSession(state => state.clearSession);

  // Use redirect URL from URL params first, then from session store
  const redirectUrl = redirectFromUrl || storeRedirectUrl;

  // Initialize input refs
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 6);
  }, []);

  // Auto-focus the first input field when component mounts
  useEffect(() => {
    // Short timeout to ensure DOM is fully loaded
    const timer = setTimeout(() => {
      if (inputRefs.current[0] && !isLoading && !isVerifying) {
        inputRefs.current[0].focus();
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [isLoading, isVerifying]);

  // Two-minute timer for code expiration
  useEffect(() => {
    // Start 2-minute timer for code expiration
    codeExpiryTimerRef.current = setTimeout(() => {
      toast.error('Verification code expired. Please try again.');
      clearSession();
      const loginParams = new URLSearchParams();
      if (isAddAccountFlow) {
        loginParams.set('addAccount', 'true');
      }
      if (adminReturnTarget) {
        loginParams.set('adminReturn', adminReturnTarget);
      }
      navigate(loginParams.toString() ? `/login?${loginParams.toString()}` : '/login');
    }, 120000); // 2 minutes in milliseconds

    return () => {
      // Clear the timer if component unmounts or verification is successful
      if (codeExpiryTimerRef.current) {
        clearTimeout(codeExpiryTimerRef.current);
      }
    };
  }, [adminReturnTarget, clearSession, isAddAccountFlow, navigate]);

  // Check if already authenticated via API on page load
  useEffect(() => {
    const verifyAuthStatus = async () => {
      if (isAddAccountFlow) {
        return;
      }
      if (isVerifying) return;
      
      try {
        // Attempt to get current user via API (which will work with HttpOnly cookies)
        const response = await authApi.getCurrentUser();
        
        if (response.success && response.data) {
          setUser(response.data);
          clearSession();

          if (await redirectToAdminReturnTarget(adminReturnTarget)) {
            return;
          }
          
          // If we have a redirect URL, use it; otherwise go to dashboard
          if (redirectUrl) {
            navigate(decodeURIComponent(redirectUrl));
          } else {
            navigate('/organizations');
          }
        }
      } catch {
        /* Auth verification errors are handled by redirecting to login */
      }
    };
    
    verifyAuthStatus();
  }, [adminReturnTarget, setUser, clearSession, navigate, isVerifying, redirectUrl, isAddAccountFlow]);

  useEffect(() => {
    // If we don't have email or session in the store, and we're not in the verification process, redirect back to login
    if (!storeEmail || !storeSession) {
      if (!isVerifying) {
        const loginParams = new URLSearchParams();
        if (isAddAccountFlow) {
          loginParams.set('addAccount', 'true');
        }
        if (adminReturnTarget) {
          loginParams.set('adminReturn', adminReturnTarget);
        }
        navigate(loginParams.toString() ? `/login?${loginParams.toString()}` : '/login');
      }
      return;
    }

    setEmail(normalizeEmail(storeEmail));
    setSession(storeSession);
  }, [adminReturnTarget, storeEmail, storeSession, navigate, isVerifying, isAddAccountFlow]);

  useEffect(() => {
    if (timeLeft > 0 && !canResend) {
      const timerId = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearTimeout(timerId);
    } else if (timeLeft === 0 && !canResend) {
      setCanResend(true);
    }
  }, [timeLeft, canResend]);

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    if (!email || !session || !code || code.length !== 6) {
      toast.error("Please enter the complete 6-digit verification code");
      return;
    }

    try {
      setIsLoading(true);
      setIsVerifying(true);
      setHasError(false);

      const isMobile = isMobileAuthFlow();
      const platform = isTauri() ? 'desktop' : isMobile ? 'mobile' : undefined;

      // Use the authApi instead of direct fetch to ensure proper cookie handling
      const response = await authApi.verifyLogin(email, code, session, platform);

      if (response.success) {
        // If login was successful, set the user in the auth context
        if (response.data) {
          if (isUserData(response.data)) {

            // Clear the code expiry timer
            if (codeExpiryTimerRef.current) {
              clearTimeout(codeExpiryTimerRef.current);
            }

            // Check if this is mobile auth flow - redirect to mobile app with tokens
            if (isMobile && response.tokens) {
              clearSession();
              const redirected = redirectToMobileApp({
                accessToken: response.tokens.accessToken,
                refreshToken: response.tokens.refreshToken,
                expiresIn: response.tokens.expiresIn,
              });

              if (redirected) {
                return;
              }
              // If redirect failed, fall through to normal web flow
              logger.warn('[LoginConfirmation] Mobile redirect failed, falling back to web flow');
            }

            // Mark verification as successful - this shows the loading logo immediately
            setVerificationSuccess(true);

            // Normal web flow - clear any previous auth state and set user in auth store
            setUser(response.data);
            useAuthStore.getState().setAuth(response.data, true, false);

            // Clear the login session
            clearSession();
            
            // Check for pending invite token
            const pendingInviteToken = localStorage.getItem('pendingInviteToken');
            if (pendingInviteToken) {
              try {
                
                // Accept the invitation
                const acceptResponse = await organizationInvitesApi.acceptInvite(pendingInviteToken);
                
                // Clear the pending invite token
                localStorage.removeItem('pendingInviteToken');
              } catch (inviteError) {
                logger.error('[LoginConfirmation] Error accepting invitation:', inviteError);
              }
            }
            
            // Determine redirect based on organization status or stored redirect URL
            try {
              // For add account flow, simply redirect to /today without complex org logic
              if (isAddAccountFlow) {
                window.location.href = '/today?auth_success=true';
                return;
              }

              if (await redirectToAdminReturnTarget(adminReturnTarget)) {
                return;
              }

              // If we have a stored redirect URL, use it directly
              if (redirectUrl) {
                
                // Check if the redirect URL contains an organization slug
                const orgSlug = extractOrgSlug(decodeURIComponent(redirectUrl));
                
                if (orgSlug) {
                  
                  // Pre-fetch organizations to ensure the org context is available
                  try {
                    const orgsResponse = await organizationsApi.getAllWithLogos({ forceRefresh: true });
                    const organizations = orgsResponse.data || [];

                    // Verify the organization exists and user has access
                    const targetOrg = organizations.find(org => org.slug === orgSlug);
                    if (targetOrg?.userMembership) {
                      window.location.href = decodeURIComponent(redirectUrl) + '?auth_success=true&prefetch=true';
                      return;
                    } else {
                      logger.warn('[LoginConfirmation] Organization not found or no access, falling back to org selection');
                    }
                  } catch (orgError) {
                    logger.error('[LoginConfirmation] Error fetching organizations for redirect:', orgError);
                  }
                }
                
                // For non-org URLs or if org verification failed, redirect directly
                window.location.href = decodeURIComponent(redirectUrl) + '?auth_success=true';
                return;
              }

              // Wait for both to complete at the same time for efficiency
              const [orgsResponse] = await Promise.all([
                organizationsApi.getAllWithLogos({ forceRefresh: true }),
                queryClient.invalidateQueries({ queryKey: queryKeys.organizationInvites.lists() })
              ]);
              const organizations = orgsResponse.data || [];

              // Determine where to redirect based on organizations
              let redirectPath = '/today?auth_success=true&prefetch=true';

              if (organizations.length === 0) {
                redirectPath = '/organization-setup?auth_success=true';
              } else if (organizations.length === 1) {
                const org = organizations[0];
                const portalEntityId = org.clientPortalAccesses?.[0]?.entityId;
                if (org.userMembership && org.slug) {
                  redirectPath = `/${org.slug}/today?auth_success=true&prefetch=true`;
                } else if (portalEntityId) {
                  redirectPath = `/client-portal/${portalEntityId}?auth_success=true&prefetch=true`;
                } else {
                  redirectPath = '/organizations?auth_success=true&prefetch=true';
                }
              } else {
                redirectPath = '/organizations?auth_success=true&prefetch=true';
              }
              window.location.href = redirectPath;
            } catch (orgError) {
              logger.error('[LoginConfirmation] Error pre-fetching data:', orgError);
              // Fallback to dashboard redirect if pre-fetching fails
              window.location.href = '/today?auth_success=true';
            }
          } else {
            setIsLoading(false);
            setIsVerifying(false);
            setHasError(true);
            toast.error('Invalid user data received');
          }
        } else {
          setIsLoading(false);
          setIsVerifying(false);
          setHasError(true);
          toast.error('No user data received');
        }
      } else {
        setIsLoading(false);
        setIsVerifying(false);
        setHasError(true);
        toast.error(response.message || 'Verification failed. Please try again.');
      }
    } catch (error) {
      logger.error('[LoginConfirmation] Error during verification:', error);
      setIsLoading(false);
      setIsVerifying(false);
      setHasError(true);
      toast.error('Error during verification. Please try again.');
    }
  };

  const handleDigitChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d+$/.test(value)) return;

    // Clear error state when user starts typing
    if (hasError) {
      setHasError(false);
    }

    // Update the code
    const newCode = code.split('');
    newCode[index] = value.charAt(0); // Just take the first character
    setCode(newCode.join(''));

    // Focus next input if there's a value and not the last digit
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    // Go to previous input on backspace if current input is empty
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const cleanedData = pastedData.replace(/\D/g, '').substring(0, 6);
    
    if (cleanedData) {
      // Fill all inputs with pasted digits
      const newCode = cleanedData.padEnd(6, ' ').substring(0, 6).split('');
      setCode(newCode.join('').trim());
      
      // Focus appropriate field
      if (cleanedData.length < 6) {
        inputRefs.current[cleanedData.length]?.focus();
      } else {
        // Focus submit button if complete
        const submitBtn = document.querySelector('button[type="submit"]') as HTMLButtonElement;
        submitBtn?.focus();
      }
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      toast.error('Email is required');
      navigate(isAddAccountFlow ? '/login?addAccount=true' : '/login');
      return;
    }

    try {
      setIsResending(true);
      const response = await authApi.resendCode(email);
      
      if (response.success) {
        toast.success('Verification code resent. Please check your email.');
        setTimeLeft(30);
        setCanResend(false);
        // Clear the current code
        setCode('');
      } else {
        toast.error(response.message || 'Failed to resend code');
      }
    } catch (error) {
      logger.error('Error resending code:', error);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('An unexpected error occurred');
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleCancel = () => {
    // Clear the session data from the store
    clearSession();
    navigate(isAddAccountFlow ? '/login?addAccount=true' : '/login');
  };

  // Show full-screen loading logo after successful verification
  // This provides a smooth transition while navigating to the dashboard
  if (verificationSuccess) {
    return <AppLoader size="lg" />;
  }

  return (
    <SplitAuthLayout pageType="login-confirmation">
      <div className="w-full">
        {/* Email display */}
        <div className="text-center mb-6">
          <p className="text-sm text-muted-foreground">
            Code sent to <span className="font-medium text-foreground">{email}</span>
          </p>
        </div>

        <form onSubmit={handleVerify} className="space-y-6">
          <div className="space-y-2">
            <div className="flex justify-center gap-2 sm:gap-3">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <Input
                  key={index}
                  ref={el => { inputRefs.current[index] = el }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className={`w-11 h-12 sm:w-12 sm:h-14 text-center text-xl font-semibold bg-background border-input ${hasError ? 'border-destructive focus-visible:ring-destructive/20' : ''}`}
                  value={code[index] || ''}
                  onChange={(e) => handleDigitChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  disabled={isLoading}
                  autoFocus={index === 0}
                />
              ))}
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-11 font-medium"
            disabled={isLoading || code.replace(/\s/g, '').length !== 6}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                <span>Verifying...</span>
              </span>
            ) : "Verify and Continue"}
          </Button>
        </form>

        <div className="flex items-center justify-between mt-6">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground hover:bg-muted"
            onClick={handleCancel}
            disabled={isLoading || isResending}
          >
            Try another email
          </Button>

          <Button
            type="button"
            variant="link"
            size="sm"
            className="text-primary h-auto p-0 hover:text-primary/80"
            onClick={handleResendCode}
            disabled={isLoading || isResending || !canResend}
          >
            {isResending ? "Sending..." : !canResend ? `Resend in ${timeLeft}s` : "Resend code"}
          </Button>
        </div>
      </div>
    </SplitAuthLayout>
  );
} 
